import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, TrendingUp, Download, Receipt, Banknote, PieChart,
  Plus, X, CheckCircle2, Search, ArrowDownLeft, ArrowUpRight,
  Ban, Gift, ExternalLink, Copy, Clock, User, Phone, Mail,
  ChevronRight, Undo2, Send, AlertTriangle, FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import SiteFilter from '@/components/admin/SiteFilter';
import TransactionDetailPanel, { SITE_LABELS, TX_TYPE_STYLES, STATUS_STYLES } from '@/components/admin/TransactionDetailPanel';

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// ── Config ──────────────────────────────────────────────────────────────────


// ── Main Page ───────────────────────────────────────────────────────────────

export default function AdminFinance() {
  const { apiFetch } = useAdmin();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeModal, setActiveModal] = useState(null); // 'payment' | 'charge' | 'refund'
  const [selectedTx, setSelectedTx] = useState(null); // Transaction detail panel

  const [txData, setTxData] = useState({ items: [], totalCount: 0, totalPages: 0, currentPage: 1 });
  const [serverKpis, setServerKpis] = useState({ grossRevenue: 0, totalRefunded: 0, netRevenue: 0, outstandingBalance: 0 });
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  const dateFrom = useMemo(() => {
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (datePreset) {
      case 'today': return sod;
      case 'week': { const d = new Date(sod); d.setDate(d.getDate() - ((sod.getDay() + 6) % 7)); return d; }
      case 'month': return new Date(now.getFullYear(), now.getMonth(), 1);
      default: return null;
    }
  }, [datePreset]);

  useEffect(() => { setPage(1); }, [siteFilter, typeFilter, debouncedSearch, datePreset]);

  const loadData = useCallback(async (silent = false) => {
    try {
      const q = siteFilter === 'all' ? '/bookings?take=200' : `/bookings?siteKey=${siteFilter}&take=200`;
      const res = await apiFetch(q);
      const data = await res.json();
      setBookings(data.items || []);
    } catch {
      setBookings([]);
    }
  }, [apiFetch, siteFilter]);

  const loadTransactions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const sp = new URLSearchParams();
      sp.set('page', page);
      sp.set('limit', 15);
      if (siteFilter !== 'all') sp.set('siteKey', siteFilter);
      if (typeFilter !== 'all') sp.set('type', typeFilter);
      if (debouncedSearch) sp.set('search', debouncedSearch);
      if (dateFrom) sp.set('dateFrom', dateFrom.toISOString());

      const [txRes, kpiRes] = await Promise.all([
        apiFetch(`/payments/transactions?${sp.toString()}`),
        apiFetch(`/payments/summary?${sp.toString()}`)
      ]);
      
      const [txResult, kpiResult] = await Promise.all([
        txRes.json(),
        kpiRes.json()
      ]);

      if (txRes.ok) setTxData(txResult);
      if (kpiRes.ok) setServerKpis(kpiResult);
    } catch (err) {
      toast.error('Failed to load transactions');
      setTxData({ items: [], totalCount: 0, totalPages: 0, currentPage: 1 });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiFetch, page, siteFilter, typeFilter, debouncedSearch, dateFrom]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);



  // Extract all transactions from bookings
  const allTransactions = useMemo(() => {
    return bookings.flatMap(b =>
      (b.paymentTransactions || []).map(t => ({
        ...t,
        patientName: `${b.patient?.firstName || ''} ${b.patient?.lastName || ''}`.trim(),
        patientEmail: b.patient?.email,
        patientPhone: b.patient?.phone,
        patientInitials: `${b.patient?.firstName?.[0] || ''}${b.patient?.lastName?.[0] || ''}`,
        serviceName: b.service,
        bookingRef: b.reference,
        bookingId: b.id,
        patientId: b.patientId,
        bookingSiteKey: b.siteKey,
        bookingStatus: b.status,
        bookingPaymentStatus: b.paymentStatus,
        bookingAmountCharged: b.amountCharged ?? b.depositAmount,
        bookingAmountPaid: b.amountPaid ?? 0,
        bookingBalance: b.balance ?? 0,
      }))
    );
  }, [bookings]);

  // Filter
  const filteredTransactions = txData.items;

  // KPIs are now fetched from server into serverKpis

  const typeCounts = useMemo(() => {
    const c = { all: allTransactions.length, payment: 0, refund: 0, adjustment: 0, void: 0, credit: 0 };
    allTransactions.forEach(t => { if (c[t.type] !== undefined) c[t.type]++; });
    return c;
  }, [allTransactions]);

  const handleExport = async () => {
    try {
      const sp = siteFilter !== 'all' ? `?siteKey=${siteFilter}` : '';
      const res = await apiFetch(`/exports/bookings/csv${sp}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `finance-export-${new Date().toISOString().split('T')[0]}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const handleModalSuccess = () => {
    setActiveModal(null);
    loadData(true);
    loadTransactions(true);
  };

  const kpis = [
    { title: 'Net Revenue', value: `£${serverKpis.netRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: `Gross: £${serverKpis.grossRevenue.toFixed(2)}`, icon: TrendingUp, gradient: 'from-emerald-700 to-emerald-800' },
    { title: 'Outstanding', value: `£${serverKpis.outstandingBalance.toFixed(2)}`, subtitle: `Pending balances`, icon: Receipt, gradient: 'from-amber-600 to-amber-700' },
    { title: 'Refunded', value: `£${serverKpis.totalRefunded.toFixed(2)}`, subtitle: `Total out`, icon: ArrowUpRight, gradient: 'from-red-600 to-red-700' },
    { title: 'Collection Rate', value: serverKpis.grossRevenue + serverKpis.outstandingBalance > 0 ? `${Math.round((serverKpis.grossRevenue / (serverKpis.grossRevenue + serverKpis.outstandingBalance)) * 100)}%` : '—', subtitle: `Revenue / Total`, icon: PieChart, gradient: 'from-stone-700 to-stone-800' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Operations</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Finance</h2>
          <p className="text-stone-500 text-sm mt-1">Transaction ledger and payment management.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <SiteFilter value={siteFilter} onChange={setSiteFilter} />
          <Button onClick={() => setActiveModal('refund')} variant="outline" className="h-10 gap-2 shrink-0 border-red-200 text-red-700 hover:bg-red-50">
            <ArrowUpRight className="w-4 h-4" /> Refund
          </Button>
          <Button onClick={() => setActiveModal('charge')} variant="outline" className="h-10 gap-2 shrink-0 border-stone-200">
            <Plus className="w-4 h-4" /> Charge
          </Button>
          <Button onClick={() => setActiveModal('payment')} className="h-10 bg-emerald-600 text-white hover:bg-emerald-700 gap-2 shrink-0">
            <Banknote className="w-4 h-4" /> Record Payment
          </Button>
          <button onClick={handleExport} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm h-10">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <motion.div key={kpi.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Card className={`bg-gradient-to-br ${kpi.gradient} text-white border-0 shadow-lg`}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-white/70">{kpi.title}</p>
                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                  </div>
                  <div className="text-3xl font-bold tracking-tight">{loading ? <Skeleton className="h-9 w-24 bg-white/20" /> : kpi.value}</div>
                  <p className="text-xs text-white/60 mt-2">{kpi.subtitle}</p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Filters Row */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
            <Input placeholder="Search by patient or reference..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-200 h-10" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{ key: 'all', label: 'All Time' }, { key: 'today', label: 'Today' }, { key: 'week', label: 'This Week' }, { key: 'month', label: 'This Month' }].map(d => (
              <button key={d.key} onClick={() => setDatePreset(d.key)}
                className={cn('text-xs font-medium px-3 py-2 rounded-lg border transition-colors', datePreset === d.key ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50')}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {/* Type filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'payment', label: 'Payments' },
            { key: 'refund', label: 'Refunds' },
            { key: 'adjustment', label: 'Adjustments' },
            { key: 'void', label: 'Voids' },
            { key: 'credit', label: 'Credits' },
          ].filter(f => f.key === 'all' || typeCounts[f.key] > 0).map(f => (
            <button key={f.key} onClick={() => setTypeFilter(f.key)}
              className={cn('text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                typeFilter === f.key ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50')}>
              {f.label} {typeCounts[f.key] > 0 && <span className="ml-1 opacity-60">{typeCounts[f.key]}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Transaction Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-stone-900">
              Transaction Ledger <span className="text-sm font-normal text-stone-400 ml-2">{txData.totalCount} records</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 text-sm">No transactions found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 bg-stone-50/50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Patient</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Reference</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredTransactions.map(tx => {
                      let ts = TX_TYPE_STYLES[tx.type] || TX_TYPE_STYLES.payment;
                      const isVoided = !!tx.voidedById || tx.status === 'voided';
                      const isReversal = tx.type === 'void';

                      // Make BOTH the original and the reversal look uniformly grey and inactive
                      if (isVoided || isReversal) {
                        ts = { ...TX_TYPE_STYLES.void, label: isReversal ? 'Void (Reversal)' : `Voided (${ts.label})` };
                      }
                      
                      const TIcon = ts.icon;

                      return (
                        <tr key={tx.id} onClick={() => setSelectedTx(tx)}
                          className={cn('hover:bg-stone-50/50 transition-colors cursor-pointer', (isVoided || isReversal) && 'opacity-50 bg-stone-50/30')}>
                          <td className="px-5 py-3.5">
                            <div className="text-xs text-stone-700 font-medium">{format(new Date(tx.createdAt), 'd MMM yyyy')}</div>
                            <div className="text-[10px] text-stone-400">{format(new Date(tx.createdAt), 'HH:mm')}</div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-full bg-stone-900 flex items-center justify-center text-white text-[10px] font-bold shrink-0">{tx.patientInitials}</div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-stone-800 truncate">{tx.patientName}</div>
                                <div className="text-[10px] text-stone-400 truncate">{tx.serviceName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            <span className="font-mono text-xs text-stone-600">{tx.bookingRef}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize', ts.bg)}>
                                <TIcon className="w-3 h-3" /> {ts.label}
                              </span>
                              {tx.status !== 'completed' && !isVoided && !isReversal && (
                                <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider', STATUS_STYLES[tx.status] || 'bg-stone-100 text-stone-500')}>
                                  {tx.status}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span className={cn('font-bold', (isVoided || isReversal) ? 'line-through text-stone-400' : ts.amountColor)}>
                              {tx.type === 'refund' ? '-' : ''}£{tx.amount?.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
          {!loading && txData.totalPages > 1 && (
            <div className="px-5 py-4 border-t border-stone-100 bg-stone-50/50 flex items-center justify-between">
              <p className="text-xs text-stone-500">
                Showing {(txData.currentPage - 1) * 15 + 1} to {Math.min(txData.currentPage * 15, txData.totalCount)} of {txData.totalCount} records
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
                <div className="text-xs font-medium text-stone-600 px-2">Page {page} of {txData.totalPages}</div>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(txData.totalPages, p + 1))} disabled={page >= txData.totalPages}>Next</Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* ── Transaction Detail Panel ── */}
      <AnimatePresence>
        {selectedTx && (
          <TransactionDetailPanel
            tx={selectedTx}
            apiFetch={apiFetch}
            onClose={() => setSelectedTx(null)}
            onUpdate={() => { setSelectedTx(null); loadData(true); loadTransactions(true); }}
            navigate={navigate}
          />
        )}
      </AnimatePresence>

      {/* ── Modals ── */}
      <AnimatePresence>
        {activeModal === 'payment' && <PaymentModal bookings={bookings} onClose={() => setActiveModal(null)} onSuccess={handleModalSuccess} />}
        {activeModal === 'charge' && <ChargeModal bookings={bookings} onClose={() => setActiveModal(null)} onSuccess={handleModalSuccess} />}
        {activeModal === 'refund' && <RefundModal bookings={bookings} onClose={() => setActiveModal(null)} onSuccess={handleModalSuccess} />}
      </AnimatePresence>
    </div>
  );
}



// ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ ═ 
// Patient Search Component (shared by all modals)
// ══════════════════════════════════════════════════════════════════════════════

function PatientBookingSearch({ bookings, onSelect, selected, filterFn, placeholder = 'Type patient name or reference...' }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const ref = useRef(null);

  const eligible = useMemo(() => filterFn ? bookings.filter(filterFn) : bookings, [bookings, filterFn]);

  const results = useMemo(() => {
    if (!query.trim()) return eligible.slice(0, 8);
    const q = query.toLowerCase();
    return eligible.filter(b =>
      `${b.patient?.firstName} ${b.patient?.lastName} ${b.reference} ${b.service}`.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [query, eligible]);

  useEffect(() => { setHighlightedIndex(0); }, [results]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlightedIndex]) {
        onSelect(results[highlightedIndex].id);
        setIsOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedBooking = bookings.find(b => b.id === selected);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="space-y-1.5" ref={ref}>
      <label className="text-xs font-medium text-stone-700">Patient / Booking</label>

      {/* Selected display */}
      {selectedBooking && !isOpen ? (
        <button type="button" onClick={() => setIsOpen(true)}
          className="w-full text-left bg-white border border-stone-200 rounded-lg px-3 py-2.5 hover:border-stone-300 transition-colors">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-stone-900 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
              {selectedBooking.patient?.firstName?.[0]}{selectedBooking.patient?.lastName?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-800">{selectedBooking.patient?.firstName} {selectedBooking.patient?.lastName}</div>
              <div className="text-[10px] text-stone-400">{selectedBooking.reference} · {selectedBooking.service}</div>
            </div>
            <X className="w-3.5 h-3.5 text-stone-400 shrink-0" onClick={(e) => { e.stopPropagation(); onSelect(''); setIsOpen(true); }} />
          </div>
        </button>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <Input
              placeholder={placeholder}
              value={query}
              onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              className="pl-9 h-10 text-sm"
              autoFocus
            />
          </div>

          {/* Dropdown */}
          {isOpen && results.length > 0 && (
            <div className="border border-stone-200 rounded-lg bg-white shadow-lg max-h-56 overflow-y-auto divide-y divide-stone-50">
              {results.map((b, index) => (
                <button key={b.id} type="button" onClick={() => { onSelect(b.id); setIsOpen(false); setQuery(''); }}
                  className={cn('w-full text-left px-3 py-2.5 transition-colors flex items-center gap-2.5', highlightedIndex === index ? 'bg-stone-100' : 'hover:bg-stone-50')}>
                  <div className="h-7 w-7 rounded-full bg-stone-900 flex items-center justify-center text-white text-[9px] font-bold shrink-0">
                    {b.patient?.firstName?.[0]}{b.patient?.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800">{b.patient?.firstName} {b.patient?.lastName}</div>
                    <div className="text-[10px] text-stone-400">{b.reference} · {b.service} · £{(b.balance ?? b.amountCharged ?? b.depositAmount)?.toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {isOpen && query.trim() && results.length === 0 && (
            <div className="border border-stone-200 rounded-lg bg-white p-4 text-center text-xs text-stone-400">
              No matching patients or bookings
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Record Payment Modal
// ══════════════════════════════════════════════════════════════════════════════

function PaymentModal({ bookings, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [selectedId, setSelectedId] = useState('');
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = bookings.find(b => b.id === selectedId);

  useEffect(() => {
    if (selected) setAmount(String(selected.balance ?? 0));
  }, [selected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId || !amount) return;
    setSaving(true);
    try {
      const res = await apiFetch('/payments/manual', { method: 'POST', body: { bookingId: selectedId, amount: parseFloat(amount), method, notes } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success(`£${parseFloat(amount).toFixed(2)} payment recorded`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} icon={Banknote} iconBg="bg-emerald-50" iconColor="text-emerald-600" title="Record Payment" subtitle="Log a manual payment received">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <PatientBookingSearch bookings={bookings} selected={selectedId} onSelect={setSelectedId}
          filterFn={b => (b.balance ?? 0) > 0} placeholder="Search patient with outstanding balance..." />

        {selected && (
          <div className="p-3 bg-stone-50 rounded-lg text-xs text-stone-600 border border-stone-100">
            Outstanding: <span className="font-bold text-amber-700">£{selected.balance?.toFixed(2)}</span> · Charged: £{(selected.amountCharged ?? selected.depositAmount)?.toFixed(2)} · Paid: £{(selected.amountPaid ?? 0).toFixed(2)}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-stone-700">Amount (£)</label>
            <Input type="number" step="0.01" min="0.01" required value={amount} onChange={e => setAmount(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-stone-700">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
              <option value="cash">Cash</option>
              <option value="card">Card (Manual)</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-stone-700">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none" placeholder="e.g. Paid in cash at reception" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button type="submit" disabled={saving || !selectedId} className="h-10 bg-emerald-600 text-white hover:bg-emerald-700 gap-2">
            {saving ? 'Recording...' : <><CheckCircle2 className="w-4 h-4" /> Confirm Payment</>}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Charge Modal
// ══════════════════════════════════════════════════════════════════════════════

function ChargeModal({ bookings, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [selectedId, setSelectedId] = useState('');
  const [amountCharged, setAmountCharged] = useState('');
  const [reason, setReason] = useState('');
  const [sendLink, setSendLink] = useState(true);
  const [saving, setSaving] = useState(false);

  const selected = bookings.find(b => b.id === selectedId);

  useEffect(() => {
    if (selected) setAmountCharged(String(selected.amountCharged ?? selected.depositAmount ?? 0));
  }, [selected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId || !amountCharged || !reason.trim()) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/payments/charge', { method: 'POST', body: { bookingId: selectedId, amountCharged: parseFloat(amountCharged), reason, sendPaymentLink: sendLink } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success(`Charge updated to £${parseFloat(amountCharged).toFixed(2)}`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} icon={Receipt} iconBg="bg-stone-100" iconColor="text-stone-600" title="Adjust Charge" subtitle="Change what a patient owes">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <PatientBookingSearch bookings={bookings} selected={selectedId} onSelect={setSelectedId}
          placeholder="Search patient to adjust charge..." />

        {selected && (
          <div className="p-3 bg-stone-50 rounded-lg text-xs text-stone-600 border border-stone-100">
            Current charge: <span className="font-bold">£{(selected.amountCharged ?? selected.depositAmount)?.toFixed(2)}</span> · Paid: £{(selected.amountPaid ?? 0).toFixed(2)}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-stone-700">New Charge Amount (£)</label>
          <Input type="number" step="0.01" min="0" required value={amountCharged} onChange={e => setAmountCharged(e.target.value)} className="h-10" />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-stone-700">Reason <span className="text-red-500">*</span></label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} required
            className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none"
            placeholder="e.g. Treatment plan changed, additional procedure" />
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="sendLink" checked={sendLink} onChange={e => setSendLink(e.target.checked)} className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
          <label htmlFor="sendLink" className="text-sm text-stone-700 cursor-pointer">Send payment link if balance remains</label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button type="submit" disabled={saving || !selectedId || !reason.trim()} className="h-10 bg-stone-900 text-white hover:bg-stone-800 gap-2">
            {saving ? 'Saving...' : <><CheckCircle2 className="w-4 h-4" /> Update Charge</>}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Refund Modal
// ══════════════════════════════════════════════════════════════════════════════

function RefundModal({ bookings, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [selectedId, setSelectedId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('manual');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = bookings.find(b => b.id === selectedId);
  const hasStripe = selected?.paymentTransactions?.some(t => t.stripePaymentIntentId && t.type === 'payment' && t.status === 'completed' && !t.voidedById);

  useEffect(() => {
    if (selected) {
      setAmount(String(selected.amountPaid ?? 0));
      setMethod(hasStripe ? 'stripe' : 'manual');
    }
  }, [selected, hasStripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId || !amount || !reason.trim()) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/payments/refund', { method: 'POST', body: { bookingId: selectedId, amount: parseFloat(amount), method, reason } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success(`£${parseFloat(amount).toFixed(2)} refunded${data.creditApplied ? ' (as patient credit)' : ''}`);
      onSuccess();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} icon={ArrowUpRight} iconBg="bg-red-50" iconColor="text-red-600" title="Issue Refund" subtitle="Refund a patient payment">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <PatientBookingSearch bookings={bookings} selected={selectedId} onSelect={setSelectedId}
          filterFn={b => (b.amountPaid ?? 0) > 0} placeholder="Search patient with payments to refund..." />

        {selected && (
          <div className="p-3 bg-red-50/50 rounded-lg text-xs text-stone-600 border border-red-100">
            Paid: <span className="font-bold text-emerald-700">£{(selected.amountPaid ?? 0).toFixed(2)}</span> · Charged: £{(selected.amountCharged ?? selected.depositAmount)?.toFixed(2)}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-stone-700">Amount (£)</label>
            <Input type="number" step="0.01" min="0.01" max={selected?.amountPaid || 999999} required value={amount} onChange={e => setAmount(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-stone-700">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
              {hasStripe && <option value="stripe">Stripe Refund</option>}
              <option value="manual">Manual Refund</option>
              <option value="credit">→ Patient Credit</option>
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-stone-700">Reason <span className="text-red-500">*</span></label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} required
            className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none"
            placeholder="Why is this refund being issued?" />
        </div>

        {method === 'credit' && (
          <p className="text-xs text-violet-600 bg-violet-50 p-3 rounded-lg border border-violet-100 flex items-center gap-2">
            <Gift className="w-4 h-4 shrink-0" /> £{parseFloat(amount || 0).toFixed(2)} will be added to the patient's credit balance for future use.
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button type="submit" disabled={saving || !selectedId || !reason.trim()} className="h-10 bg-red-600 text-white hover:bg-red-700 gap-2">
            {saving ? 'Processing...' : <><ArrowUpRight className="w-4 h-4" /> Issue Refund</>}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Modal Shell ─────────────────────────────────────────────────────────────

function ModalShell({ children, onClose, icon: Icon, iconBg, iconColor, title, subtitle }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', iconBg)}><Icon className={cn('w-5 h-5', iconColor)} /></div>
                <div><h3 className="font-playfair text-lg font-medium text-stone-900">{title}</h3><p className="text-xs text-stone-500">{subtitle}</p></div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400"><X size={18} /></button>
            </div>
          </div>
          {children}
        </div>
      </motion.div>
    </>
  );
}
